import React from 'react';
import Button from '@material-ui/core/Button';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import Paper from '@material-ui/core/Paper';
import { makeStyles, type Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { useParams } from 'react-router-dom';
import {
  HandOverDeviceFlow,
  candidatesFromRecipients,
  useCanSignInPersonOn,
  useInPersonSigning,
  type IInPersonCandidate,
} from '../../common/inPersonSigning';
import { LaunchDarklyContext } from '../../common/launchDarkly';
import { useShallowSelector, useDispatch } from '../../lib/reduxHooks';
import { getTimezoneCode, generateFileName } from '../../lib/utils';
import type { IRootState } from '../../store';
import * as actions from '../../store/envelopeHistory/actions';
import {
  type IEnvelopeHistoryBody,
  type IRecipientBody,
  type IAuditHistoryBody,
} from '../../store/envelopeHistory/types';
import { StyledMenu, StyledMenuItem } from '../common/StyledMenu';

dayjs.extend(advancedFormat);

const useStyles = makeStyles((theme: Theme) => ({
  paper: {
    backgroundColor: 'transparent',
    boxShadow: 'none',
    marginTop: theme.spacing(4),
  },
  actionButtons: {
    display: 'flex',
    alignItems: 'center',
  },
  summary: {
    flexGrow: 1,
  },
  divider: {
    borderLeft: '1px solid #D8D9DB',
    margin: '0px 15px 0px 15px',
    height: '40px',
  },
  gridItem: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
  },
  container: {
    paddingBottom: theme.spacing(1),
    paddingLeft: theme.spacing(3),
  },
  checkCircleIcon: {
    fontSize: '28px',
    paddingRight: theme.spacing(1),
  },
  listItem: {
    paddingLeft: 0,
    paddingBottom: 0,
  },
  icon: {
    margin: '-4px 0 -4px 4px',
  },
  signInPerson: {
    marginLeft: theme.spacing(1),
    padding: 0,
    minWidth: 0,
    fontWeight: 'bold',
    textTransform: 'none',
    verticalAlign: 'baseline',
    '&:hover': { background: 'none', textDecoration: 'underline' },
  },
  separator: {
    marginLeft: theme.spacing(1),
    color: '#8A94A6',
  },
}));

interface IState {
  isFetching: boolean;
  envelope: IEnvelopeHistoryBody;
  auditHistory: IAuditHistoryBody[];
}

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const selector = (state: IRootState) => ({
  isFetching: state.envelopeHistory.isFetching,
  envelope: state.envelopeHistory.envelope,
  auditHistory: state.envelopeHistory.auditHistory,
});

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const SummaryRecipientList = () => {
  const store: IState = useShallowSelector(selector);
  const classes = useStyles();
  const { flags } = React.useContext(LaunchDarklyContext);
  const { recipients } = store.envelope;

  // Same hand-off the dashboard offers. The sender's own row is left out: that
  // would record them as their own witness, and Sign Now already covers it.
  //
  // Scoped to this envelope, unlike the dashboard: a Forms permission delegate
  // reads as isDelegate false, and here we can see whose envelope it is, so they
  // no longer get a link the service answers with 403 (SK-2687).
  const canSignInPerson = useCanSignInPersonOn(flags, store.envelope.senderId);
  const inPersonSigners = canSignInPerson
    ? candidatesFromRecipients(recipients, store.envelope.senderEmail, store.envelope.signerDetails).filter(
        // An action, so only for whoever can take the device now.
        (c) => c.isEligible && !c.isSender
      )
    : [];

  // One hook for the whole list, so the in-flight guard covers every row.
  const inPersonSigning = useInPersonSigning();

  const recipientsWithEnvelopeDownloadedEvents = store.auditHistory
    .filter((e) => e.eventName == 'EnvelopeDownloaded')
    .map((e) => e.userId);

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const handleStatusCheck = (r: IRecipientBody) => {
    return r.status === 'Complete' || (r.role == 'CarbonCopy' && recipientsWithEnvelopeDownloadedEvents.includes(r.id))
      ? '#47B676'
      : '#DEE5ED';
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const handleSort = (firstRecipient: IRecipientBody, secondRecipient: IRecipientBody) => {
    const firstRole = firstRecipient.role;
    const secondRole = secondRecipient.role;

    if (firstRole > secondRole) return -1;
    if (firstRole < secondRole) return 1;
    return 0;
  };

  const sortedRecipients = [...recipients].sort(handleSort);

  return (
    <Container component="div" className={classes.container}>
      <Typography variant="body2">To</Typography>
      <List>
        {sortedRecipients.map((r: IRecipientBody) => (
          <ListItem key={r.id} className={classes.listItem}>
            <CheckCircleIcon
              data-spec="checkCircle"
              className={classes.checkCircleIcon}
              style={{ color: handleStatusCheck(r) }}
            />
            <Typography>
              {r.role === 'CarbonCopy' ? 'Cc: ' : ''}
              {r.firstName} {r.middleName} {r.lastName} ({r.email})
            </Typography>
            <SignInPersonLink
              signer={inPersonSigners.find((c) => c.signerId === r.id)}
              classes={classes}
              startingSignerId={inPersonSigning.startingSignerId}
              isStarting={inPersonSigning.isStarting}
              onStart={(signer) => inPersonSigning.requestHandOff(store.envelope.id, signer, 'envelopeHistory')}
            />
          </ListItem>
        ))}
      </List>
      {/* Every entry point confirms the device changing hands, this one included. */}
      <HandOverDeviceFlow signing={inPersonSigning} />
    </Container>
  );
};

function SignInPersonLink({
  signer,
  classes,
  onStart,
  startingSignerId,
  isStarting,
}: {
  signer?: IInPersonCandidate;
  classes: Record<string, string>;
  onStart: (signer: IInPersonCandidate) => void;
  startingSignerId: string | null;
  isStarting: boolean;
}) {
  if (!signer) return null;

  return (
    <>
      <span className={classes.separator} aria-hidden>
        &bull;
      </span>
      <Button
        data-spec={`sign-in-person-${signer.signerId}`}
        color="primary"
        disableRipple
        className={classes.signInPerson}
        disabled={isStarting}
        onClick={() => onStart(signer)}
      >
        {startingSignerId === signer.signerId ? 'Starting\u2026' : 'Sign In Person'}
      </Button>
    </>
  );
}

export function formattedDateForHistory(dateString: string): string {
  try {
    const date = dayjs(dateString);
    return date.format('MMMM Do, YYYY h:mm A');
  } catch {
    return '';
  }
}

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const SentDateAndSenderInfo = () => {
  const classes = useStyles();
  const store: IState = useShallowSelector(selector);
  const { senderFirstName, senderMiddleName, senderLastName, senderEmail, sentDate, completedDate } = store.envelope;
  const envelopeSentDate = formattedDateForHistory(sentDate);

  const envelopeCompletedDate = completedDate ? formattedDateForHistory(completedDate) : null;
  const timeZone = getTimezoneCode();

  return (
    <>
      <Container component="div" className={classes.container}>
        {store.isFetching ? (
          ''
        ) : completedDate ? (
          <>
            <Typography variant="body2">Completed On</Typography>
            <Typography>
              {envelopeCompletedDate} {timeZone}
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="body2">Sent On</Typography>
            <Typography>
              {envelopeSentDate} {timeZone}
            </Typography>
          </>
        )}
      </Container>
      <Container component="div" className={classes.container}>
        <Typography variant="body2">By</Typography>
        <Typography data-spec="senderName">
          {senderFirstName} {senderMiddleName} {senderLastName}
        </Typography>
        <Typography>({senderEmail})</Typography>
      </Container>
    </>
  );
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const ActionButtons = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const store: IState = useShallowSelector(selector);
  const params = useParams<{ id: string }>();
  const [downloadAnchorEl, setDownloadAnchorEl] = React.useState(null);
  const [printAnchorEl, setPrintAnchorEl] = React.useState(null);
  const canDownloadEnvelopeAndCert: string[] = ['sent', 'inprogress', 'completed'];
  const status = store.envelope.status.toLowerCase();
  const printFile = true;

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const handleDownloadClick = (event: any) => {
    setDownloadAnchorEl(event.currentTarget);
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const handlePrintClick = (event: any) => {
    setPrintAnchorEl(event.currentTarget);
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const downloadEnvelope = (print?: boolean) => {
    const envelopeFileName = generateFileName(store.envelope.documents);
    dispatch(actions.downloadEnvelope(params.id, envelopeFileName, print));
  };

  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const downloadCertificate = (print?: boolean) => {
    const certificateFileName = 'DigiSignAuditCertificate.pdf';
    dispatch(actions.downloadCertificate(params.id, certificateFileName, print));
  };

  return (
    <>
      <Button data-spec="print-button" variant="outlined" color="primary" onClick={handlePrintClick}>
        Print
        <ExpandMoreIcon className={classes.icon} />
      </Button>
      <StyledMenu
        anchorEl={printAnchorEl}
        open={Boolean(printAnchorEl)}
        onClose={() => setPrintAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        getContentAnchorEl={null}
      >
        {canDownloadEnvelopeAndCert.includes(status) ? (
          <>
            <StyledMenuItem onClick={() => downloadEnvelope(printFile)}>All Documents</StyledMenuItem>
            <StyledMenuItem onClick={() => downloadCertificate(printFile)}>Certificate</StyledMenuItem>
          </>
        ) : (
          <StyledMenuItem onClick={() => downloadEnvelope(printFile)}>All Documents</StyledMenuItem>
        )}
      </StyledMenu>
      <div className={classes.divider} />
      <Button data-spec="download-button" variant="outlined" color="primary" onClick={handleDownloadClick}>
        Download
        <ExpandMoreIcon className={classes.icon} />
      </Button>
      <StyledMenu
        anchorEl={downloadAnchorEl}
        open={Boolean(downloadAnchorEl)}
        onClose={() => setDownloadAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        getContentAnchorEl={null}
      >
        {canDownloadEnvelopeAndCert.includes(status) ? (
          <>
            <StyledMenuItem onClick={() => downloadEnvelope()}>All Documents</StyledMenuItem>
            <StyledMenuItem onClick={() => downloadCertificate()}>Certificate</StyledMenuItem>
          </>
        ) : (
          <StyledMenuItem onClick={() => downloadEnvelope()}>All Documents</StyledMenuItem>
        )}
      </StyledMenu>
    </>
  );
};

export function Summary() {
  const classes = useStyles();

  return (
    <>
      <div className={classes.actionButtons}>
        <Typography className={classes.summary} variant="overline">
          Summary
        </Typography>
        <ActionButtons />
      </div>
      <Paper square variant="outlined" className={classes.paper}>
        <Grid container>
          <Grid item xs={12} sm={6} className={classes.gridItem}>
            <SentDateAndSenderInfo />
          </Grid>
          <Grid item xs={12} sm={6} className={classes.gridItem}>
            <SummaryRecipientList />
          </Grid>
        </Grid>
      </Paper>
    </>
  );
}
